import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ExpandableLogRow } from '@/components/logs/ExpandableLogRow';
import { LogFilters } from '@/components/logs/LogFilters';
import { useLogData } from '@/hooks/useLogData';
import { formatJST } from '@/lib/date';

/**
 * Log search page
 * Page for searching and displaying logs
 */
export function Logs() {
  // Use log data hook
  const {
    logs,
    loading,
    error,
    totalCount,
    page,
    rowsPerPage,
    filters,
    setPage,
    setRowsPerPage,
    setFilters,
    fetchLogs,
    resetFilters,
  } = useLogData();

  // Page change handler
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Rows per page change handler
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const { t } = useTranslation(['logs', 'common']);

  // Format dates for text input (YYYY-MM-DD HH:mm:ss)
  const formattedSince = filters.since ? formatJST(filters.since, 'YYYY-MM-DD HH:mm:ss') : '';
  const formattedUntil = filters.until ? formatJST(filters.until, 'YYYY-MM-DD HH:mm:ss') : '';

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        {t('logs:title')}
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <LogFilters
          level={filters.level}
          since={formattedSince}
          until={formattedUntil}
          onFilterChange={setFilters}
          onSearch={fetchLogs}
          onReset={resetFilters}
        />
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.message}
        </Alert>
      )}

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table
            sx={{
              minWidth: 650,
              '& .MuiTableCell-root': { py: 0.5 }, // Apply to all TableCells
            }}
              aria-label={t('logs:table.ariaLabel')}
            size="small"
          >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>{t('logs:table.timestamp')}</TableCell>
                <TableCell>{t('logs:table.level')}</TableCell>
                <TableCell>{t('logs:table.message')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={24} />
                    <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>
                      {t('logs:messages.loading')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {t('logs:table.noLogs')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <ExpandableLogRow key={log.id} log={log} />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common:pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => t('common:pagination.displayedRows', { from, to, count })}
        />
      </Paper>
    </Box>
  );
}

export default Logs;
